import { resolveRouteComponent } from "v4/features/front/navigation/utils/navigation.mapper";

/**
 * Détermine si un item de navigation correspond à une route de référence.
 * 
 * @param {Object} navItem - L'item du menu (serveur ou statique).
 * @param {Object} referenceRoute - La route à comparer (currentRoute.route ou activeListingRoute).
 * @returns {boolean}
 */
export const isMatching = (navItem, referenceRoute) => {
    if (!referenceRoute) return false;

    const navNative = navItem.native_identifier;
    const refNative = referenceRoute.native_identifier;
    const navId = navItem.metadata?.id;
    const refId = referenceRoute.metadata?.id;

    // 1. Match par identifiant natif (Source de vérité absolue pour les routes système)
    if (navNative && refNative) return navNative === refNative;

    // 2. Match par ID de tab (Source de vérité pour les onglets personnalisés)
    if (navId && refId) return navId === refId;

    // 3. Si l'item du menu est un onglet personnalisé (possède un ID), 
    // il ne doit matcher QUE si l'ID est identique. On refuse le fallback par type
    // pour éviter que tous les onglets d'un même type ne s'allument.
    if (navId) return navId === refId;

    // 4. Fallback par search_type uniquement pour les menus NATIFS.
    // Cela permet au menu "Prospects" (système) de rester actif même si on est
    // dans une sous-vue personnalisée ou un détail.
    if (navNative && navItem.search_type && navItem.search_type === referenceRoute.search_type) {
        const navConfig = resolveRouteComponent(navItem);
        const refConfig = resolveRouteComponent(referenceRoute);
        return navConfig?.component === refConfig?.component;
    }

    return false;
};

/**
 * Parcourt récursivement un arbre de navigation pour trouver un item selon un prédicat.
 * 
 * @param {Array} items - L'arbre de navigation.
 * @param {Function} predicate - Fonction de test (item) => boolean.
 * @returns {Object|null}
 */
export const findNavigationItem = (items, predicate) => {
    if (!items || !Array.isArray(items)) return null;

    for (const item of items) {
        if (predicate(item)) return item;
        if (item.children?.length) {
            const found = findNavigationItem(item.children, predicate);
            if (found) return found;
        }
    }
    return null;
};

/**
 * Transforme un pattern d'URL (avec placeholders {key} et wildcards *) en Expression Régulière.
 *
 * @param {string} pattern - Le pattern d'URL (ex: 'clients-prospects/{prospect}/*').
 * @param {boolean} allowSubPaths - Si true, autorise des segments supplémentaires après le pattern.
 * @returns {RegExp}
 */
export const patternToRegex = (pattern, allowSubPaths = false) => {
    if (!pattern) return /^$/;
    const cleanPattern = pattern.replace(/^\/+|\/+$/g, '');
    const regexPattern = cleanPattern
        .replace(/[.+?^${}()|[\]\\]/g, '\\$&') // Échappement des caractères spéciaux Regex (sauf *)
        .replace(/\\\{[a-zA-Z0-9_-]+\\}/g, '([^/]+)') // Remplacement des placeholders {key}
        .replace(/\*/g, '.*'); // Remplacement des wildcards *

    return new RegExp(`^${regexPattern}${allowSubPaths ? '(/.*)?' : ''}/?$`);
};

/**
 * Calcule les métadonnées de matching pour un pattern (score de spécificité et nombre de placeholders).
 * 
 * @param {string} pattern 
 * @returns {{score: number, placeholderCount: number}}
 */
export const getPatternMetadata = (pattern) => {
    const cleanPattern = pattern.replace(/^\/+|\/+$/g, '');
    return {
        score: cleanPattern.split('/').filter(Boolean).length,
        placeholderCount: (cleanPattern.match(/\{[a-zA-Z0-9_-]+\}/g) || []).length
    };
};
